// Fila editable de un artículo del Anexo.
//
// Portado de ItemRow.jsx de Recepción Digital: mismo flujo de teclado, mismo
// recorte de descripción a 60 caracteres al escribir, mismo total derivado de
// solo lectura, y el mismo borrado de dos toques (armar → confirmar, con
// desarme automático a los 4 s) en vez de un modal.
//
// Nuevo aquí: el typeahead contra customs_catalog. No existía nada equivalente
// que portar — la descripción en Recepción Digital es un input de texto plano.

import { useEffect, useRef, useState } from 'react';
import { Trash2, Check } from 'lucide-react';
import { supabase } from '../../supabaseClient';
import { ANEXO_ITEM_GRID } from './grid';

const MAX_DESC = 60;

export const UNIDADES = ['UNIDAD', 'KG', 'LB', 'PAR', 'JUEGO', 'DOCENA'];

const inputBase =
  'rounded-md border border-slate-200 bg-[#f8fafc] px-2 py-1.5 text-xs text-slate-900 ' +
  'outline-none focus:border-[var(--brand-teal)] transition-colors';

const AnexoItemRow = ({ item, onChange, onQuitar, onEnterNewRow, autoFocus }) => {
  const [confirmarQuitar, setConfirmarQuitar] = useState(false);
  const [sugerencias, setSugerencias] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [resaltado, setResaltado] = useState(0);
  const [enfocado, setEnfocado] = useState(false);
  const descRef = useRef(null);
  const cantidadRef = useRef(null);
  const escribiendoRef = useRef(false);

  useEffect(() => {
    if (autoFocus) descRef.current?.focus();
  }, [autoFocus]);

  // Desarmar el borrado si el operador sigue trabajando.
  useEffect(() => {
    if (!confirmarQuitar) return undefined;
    const t = setTimeout(() => setConfirmarQuitar(false), 4000);
    return () => clearTimeout(t);
  }, [confirmarQuitar]);

  // Sugerencias inline: 200 ms de espera, ilike sobre descripcion, top 3.
  // Antes esto era un dropdown absoluto; ahora es una tira de chips debajo del
  // input, dentro de la columna de descripción — la misma forma en la que va a
  // aparecer el top-3 de RAG cuando llegue.
  const termino = item.descripcion || '';
  useEffect(() => {
    if (!escribiendoRef.current || !enfocado) return undefined;
    if (item.customs_catalog_id) {
      setSugerencias([]);
      return undefined;
    }
    const q = termino.trim();
    if (q.length < 3) {
      setSugerencias([]);
      return undefined;
    }
    const t = setTimeout(async () => {
      const { data, error } = await supabase
        .from('customs_catalog')
        .select('id, descripcion, unidad_medida, fob_usd, cip_usd')
        .ilike('descripcion', `%${q}%`)
        .limit(3);
      if (error) {
        console.error('[Desarrollos/ledger] error buscando en el catálogo', error);
        return;
      }
      setSugerencias(data || []);
      setResaltado(0);
      setAbierto(true);
    }, 200);
    return () => clearTimeout(t);
  }, [termino, enfocado, item.customs_catalog_id]);

  // Ligar NO sobreescribe `descripcion`: el texto crudo del operador queda
  // intacto y la vista ligada muestra catalogo_descripcion (mismo contrato que
  // el drawer de extracción). Desligar lo restaura solo.
  const elegirSugerencia = (row) => {
    escribiendoRef.current = false;
    onChange({
      customs_catalog_id: row.id,
      catalogo_descripcion: row.descripcion ?? null,
      catalogo_unidad_medida: row.unidad_medida ?? null,
      manual_override: false,
      unidad_medida: row.unidad_medida || item.unidad_medida || 'UNIDAD',
      fob_unitario_usd: Number(row.fob_usd) || 0,
      cip_unitario_usd: Number(row.cip_usd) || null,
    });
    setAbierto(false);
    setSugerencias([]);
    // El siguiente dato que el operador necesita teclear es la cantidad.
    cantidadRef.current?.focus();
  };

  const onKeyDownDesc = (e) => {
    if (abierto && sugerencias.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setResaltado((i) => (i + 1) % sugerencias.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setResaltado((i) => (i - 1 + sugerencias.length) % sugerencias.length);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        elegirSugerencia(sugerencias[resaltado]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setAbierto(false);
        return;
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      onEnterNewRow && onEnterNewRow();
    }
  };

  const cantidad = Number(item.cantidad) || 0;
  const unitario = Number(item.fob_unitario_usd) || 0;
  const total = cantidad * unitario;

  // Ligado al catálogo: el input queda de solo lectura en verde mostrando la
  // descripción del catálogo; `descripcion` (el texto crudo) vive intacto
  // debajo. Para editar hay que Desligar primero (CatalogHoverPicker).
  const ligado = Boolean(item.customs_catalog_id);

  return (
    <div className={`grid ${ANEXO_ITEM_GRID} items-center gap-2 rounded-md px-1.5 py-1 hover:bg-slate-50`}>
      {/* Descripción + typeahead */}
      <div className="relative min-w-0">
        <input
          ref={descRef}
          type="text"
          value={ligado ? (item.catalogo_descripcion ?? item.descripcion) || '' : item.descripcion || ''}
          readOnly={ligado}
          onChange={(e) => {
            escribiendoRef.current = true;
            // Al teclear libremente el artículo deja de estar ligado al catálogo.
            onChange({
              descripcion: e.target.value.slice(0, MAX_DESC),
              customs_catalog_id: null,
              manual_override: true,
            });
          }}
          onKeyDown={onKeyDownDesc}
          onFocus={() => setEnfocado(true)}
          onBlur={() => {
            // 150 ms para que el click en un chip alcance a registrarse.
            setTimeout(() => {
              setEnfocado(false);
              setAbierto(false);
            }, 150);
          }}
          placeholder="Descripción del artículo"
          aria-label="Descripción del artículo"
          title={ligado ? (item.catalogo_descripcion ?? item.descripcion) || '' : item.descripcion || ''}
          maxLength={MAX_DESC}
          className={
            ligado
              ? 'w-full overflow-hidden text-ellipsis rounded-md border border-green-300 bg-green-50 px-2 py-1.5 text-xs text-green-800 outline-none transition-colors'
              : `${inputBase} w-full overflow-hidden text-ellipsis`
          }
        />
        {abierto && enfocado && sugerencias.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center gap-1">
            <span className="text-[11px] text-slate-400">Sugerencias:</span>
            {sugerencias.map((s, i) => {
              const corto =
                (s.descripcion || '').length > 28
                  ? `${s.descripcion.slice(0, 28)}…`
                  : s.descripcion;
              return (
                <button
                  key={s.id}
                  type="button"
                  title={s.descripcion}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => elegirSugerencia(s)}
                  className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors ${
                    i === resaltado
                      ? 'border-[var(--brand-teal)] bg-[var(--brand-teal-soft)] text-slate-900'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  {corto}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Cantidad — NUMERIC(10,3) */}
      <input
        ref={cantidadRef}
        type="number"
        min={0.001}
        step={0.001}
        value={item.cantidad ?? ''}
        onChange={(e) => onChange({ cantidad: e.target.value })}
        onKeyDown={onKeyDownDesc}
        aria-label="Cantidad"
        className={`${inputBase} w-full text-right font-mono`}
      />

      {/* Unidad */}
      <select
        value={item.unidad_medida || 'UNIDAD'}
        onChange={(e) => onChange({ unidad_medida: e.target.value })}
        aria-label="Unidad de medida"
        className={`${inputBase} w-full`}
      >
        {UNIDADES.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>

      {/* FOB unitario */}
      <div className="relative">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">
          $
        </span>
        <input
          type="number"
          min={0}
          step={0.01}
          value={item.fob_unitario_usd ?? ''}
          onChange={(e) => onChange({ fob_unitario_usd: e.target.value })}
          onKeyDown={onKeyDownDesc}
          aria-label="Valor FOB unitario"
          className={`${inputBase} w-full pl-5 text-right font-mono`}
        />
      </div>

      {/* Total derivado */}
      <span
        className="text-right font-mono text-xs font-semibold text-slate-900"
        aria-label={`Total del artículo: ${total.toFixed(2)} dólares`}
      >
        {total.toFixed(2)}
      </span>

      {/* Borrado de dos toques */}
      <div className="flex items-center justify-end">
        {confirmarQuitar ? (
          <button
            type="button"
            onClick={onQuitar}
            aria-label="Confirmar eliminación del artículo"
            className="rounded-md border border-red-300 bg-red-50 px-1.5 py-1 text-[10px] font-semibold text-red-600"
          >
            Confirmar
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmarQuitar(true)}
            aria-label="Eliminar artículo"
            title="Eliminar artículo"
            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={13} aria-hidden="true" />
          </button>
        )}
        {item.customs_catalog_id && (
          <Check size={12} className="ml-1 text-green-600" aria-label="Ligado al catálogo" />
        )}
      </div>
    </div>
  );
};

export default AnexoItemRow;
